import React, { useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import {
  BookOpen, LogIn, LayoutDashboard, Users, Radio, Palette, Monitor, Youtube,
  HelpCircle, ChevronRight, Copy, CheckCircle2, AlertTriangle, Video, Sparkles,
  Zap, ArrowRight
} from "lucide-react";

const SECTIONS = [
  { id: "welcome", label: "Welcome", icon: Sparkles },
  { id: "account", label: "1. Getting Your Account", icon: LogIn },
  { id: "login", label: "2. Signing In", icon: LogIn },
  { id: "dashboard", label: "3. Your Match Dashboard", icon: LayoutDashboard },
  { id: "create-match", label: "4. Creating a Match", icon: Radio },
  { id: "setup", label: "5. Match & Toss Setup", icon: HelpCircle },
  { id: "teams", label: "6. Team Config & Lineups", icon: Users },
  { id: "scoring", label: "7. Live Scoring", icon: Zap },
  { id: "director", label: "8. Director Switcher", icon: Video },
  { id: "themes", label: "9. Graphics Themes", icon: Palette },
  { id: "overlay", label: "10. Your Overlay URL", icon: Copy },
  { id: "vmix", label: "11. Setup in vMix", icon: Monitor },
  { id: "obs", label: "12. Setup in OBS Studio", icon: Monitor },
  { id: "youtube", label: "13. Going Live on YouTube", icon: Youtube },
  { id: "workflow", label: "14. Real Match Workflow", icon: ArrowRight },
  { id: "troubleshoot", label: "15. Troubleshooting", icon: AlertTriangle },
];

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white font-heading font-black flex items-center justify-center">{n}</div>
      <div className="flex-1">
        <div className="font-heading font-bold text-lg mb-1 text-white">{title}</div>
        <div className="text-slate-300 leading-relaxed text-[15px]">{children}</div>
      </div>
    </div>
  );
}

function Callout({ tone = "info", title, children }) {
  const tones = {
    info:   "bg-blue-500/10 border-blue-800 text-blue-200",
    warn:   "bg-amber-500/10 border-amber-800 text-amber-200",
    good:   "bg-emerald-500/10 border-emerald-800 text-emerald-200",
    danger: "bg-red-500/10 border-red-800 text-red-200",
  };
  const icons = { info: HelpCircle, warn: AlertTriangle, good: CheckCircle2, danger: AlertTriangle };
  const Icon = icons[tone];
  return (
    <div className={`p-4 rounded-lg border ${tones[tone]} flex gap-3 my-4`}>
      <Icon size={18} className="flex-shrink-0 mt-0.5" />
      <div>
        {title && <div className="font-semibold mb-1">{title}</div>}
        <div className="text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-16">
      <div className="flex items-center gap-3 mb-5 pb-3 border-b border-slate-800">
        <div className="w-10 h-10 rounded-lg bg-blue-600/15 border border-blue-800 flex items-center justify-center text-blue-400">
          <Icon size={20} />
        </div>
        <h2 className="font-heading text-3xl font-extrabold uppercase tracking-tight text-white">{title}</h2>
      </div>
      <div className="space-y-4 text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

export default function ManualPage() {
  const [active, setActive] = useState("welcome");

  return (
    <div className="min-h-screen bg-slate-950">
      <NavBar subtitle="User Manual" />
      <main className="pt-20 max-w-7xl mx-auto px-4 lg:px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8 pt-6">
          {/* Sticky Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="glass rounded-xl border border-slate-800 p-3">
              <div className="flex items-center gap-2 px-3 py-2 mb-2 text-xs uppercase text-slate-400 font-bold tracking-wider">
                <BookOpen size={14} /> Contents
              </div>
              <nav className="space-y-0.5 max-h-[75vh] overflow-y-auto">
                {SECTIONS.map(s => {
                  const I = s.icon;
                  return (
                    <a key={s.id} href={`#${s.id}`}
                      onClick={() => setActive(s.id)}
                      data-testid={`toc-${s.id}`}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition
                        ${active === s.id ? "bg-blue-600 text-white font-semibold" : "text-slate-300 hover:bg-slate-800"}`}>
                      <I size={14} />
                      <span className="truncate">{s.label}</span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div>
            <Section id="welcome" icon={Sparkles} title="Welcome to Cricket Graphics Studio">
              <p>This manual walks you through everything from creating your account to running a professional broadcast on YouTube Live using vMix or OBS Studio. Whether you're covering a local T20 or a full season, this guide will get you on-air in under 30 minutes.</p>
              <Callout tone="info" title="What you'll learn">
                Account activation → creating a match → adding team lineups → live scoring → switching graphics on-air → putting the overlay in vMix/OBS → streaming to YouTube.
              </Callout>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                {[
                  { icon: Zap, title: "Instant Sync", desc: "Every score update reflects on your overlay in real-time via Firebase" },
                  { icon: Palette, title: "6 Broadcast Themes", desc: "Sky Live, IPL Neon, ESPN, Broadcast Pro, Minimalist + Classic" },
                  { icon: Monitor, title: "vMix + OBS Ready", desc: "Just paste the overlay URL as a Browser Source and you're live" },
                ].map((f, i) => (
                  <div key={i} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <f.icon className="text-blue-400 mb-2" size={20} />
                    <div className="font-heading font-bold text-white">{f.title}</div>
                    <div className="text-sm text-slate-400 mt-1">{f.desc}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="account" icon={LogIn} title="1. Getting Your Account">
              <p>Cricket Graphics Studio is a subscriber service. Accounts are activated by the platform administrator with a specific expiry date (e.g., 30 / 90 / 365 days).</p>
              <Step n="1" title="Contact your administrator">Reach out to the admin who onboarded you. Provide your email address, the name you'd like on the account, and the duration you've paid for.</Step>
              <Step n="2" title="Wait for confirmation">The admin will create your account and share your email + temporary password. You'll receive access instantly.</Step>
              <Step n="3" title="Note your expiry date">Your subscription runs until the date the admin set. You'll always see days-left in the top-right corner after login.</Step>
              <Callout tone="warn" title="Subscription expired?">
                If your access expires, you'll be unable to log in. Contact your administrator to extend — they can add days in one click.
              </Callout>
            </Section>

            <Section id="login" icon={LogIn} title="2. Signing In">
              <p>Go to the login URL your admin shared with you.</p>
              <Step n="1" title="Enter your email + password">Both are case-sensitive. Type carefully.</Step>
              <Step n="2" title="Click Sign In">You'll be taken to your Match Dashboard.</Step>
              <Callout tone="info">
                Top-right of every page: your expiry badge (green ≥ 8 days, amber ≤ 7, red ≤ 3, red "Expired" when past).
              </Callout>
            </Section>

            <Section id="dashboard" icon={LayoutDashboard} title="3. Your Match Dashboard">
              <p>The dashboard is where you'll spend most of your time between matches. It shows every match you've created — Live, Draft, and Completed.</p>
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 space-y-2 text-sm">
                <div><b className="text-white">Subscription banner (top):</b> shows exactly how many days you have left. Turns amber and red as expiry approaches.</div>
                <div><b className="text-white">New Match button:</b> creates a fresh match.</div>
                <div><b className="text-white">Match cards:</b> each shows the two teams, overs, venue. Click <i>Open Controller</i> to run it, <i>Copy Overlay URL</i> to grab the vMix/OBS link, or the delete icon to remove it.</div>
              </div>
            </Section>

            <Section id="create-match" icon={Radio} title="4. Creating a Match">
              <Step n="1" title="Click 'New Match'">A dialog will appear.</Step>
              <Step n="2" title="Fill in match details">Match name (e.g., "1st T20I"), Team 1 full + short name, Team 2 full + short name, total overs (1–50), and venue.</Step>
              <Step n="3" title="Click Create & Open">You'll be taken straight to the Cricket Controller where all live scoring happens.</Step>
              <Callout tone="good">
                Match names appear on your overlay's Match Summary and Batting/Bowling cards, so make them clean (e.g., "Final", "Group Match 5").
              </Callout>
            </Section>

            <Section id="setup" icon={HelpCircle} title="5. Match & Toss Setup Tab">
              <p>The Controller opens on the <b>Toss & Match Setup</b> tab. Configure once per match, before the first ball.</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li><b>Total Overs per Innings:</b> normally 20 for T20 or 50 for ODI.</li>
                <li><b>Match Name / Venue:</b> already filled from the create dialog — edit if needed.</li>
                <li><b>Toss Won By + Elected To:</b> select the winning team and whether they chose to bat or bowl.</li>
                <li>Click <b>Save Match & Toss Setup</b>. This locks the batting/bowling roles.</li>
              </ul>
              <Callout tone="warn" title="Reset Entire Match">
                The red button wipes everything back to zero. Only use it before your innings starts, or if you've made a mistake at setup.
              </Callout>
            </Section>

            <Section id="teams" icon={Users} title="6. Team Config & Lineups Tab">
              <p>Switch to the <b>Team Config & Lineups</b> tab.</p>
              <div className="space-y-3">
                <Step n="1" title="Team Details sub-tab">Set each team's full name (INDIA), short name (IND), theme color (this drives the scorebug / summary card colors), and upload a logo PNG or JPG.</Step>
                <Step n="2" title="Save Team Config">Click the green button. Your overlay updates instantly.</Step>
                <Step n="3" title="Lineups sub-tab">Type all 11 player names for both teams. Use ▲▼ to reorder. Names appear on the scorebug, batting/bowling summary and PDF scorecard.</Step>
              </div>
              <Callout tone="info" title="Pro tip">
                Use consistent short names (initials + surname): "R Sharma", "V Kohli". They read beautifully on the scorebug.
              </Callout>
            </Section>

            <Section id="scoring" icon={Zap} title="7. Live Scoring — the Match Scoring Tab">
              <p>This is where every ball is entered. Before your first ball, click <b>Select Active Players</b> and pick the striker, non-striker, and opening bowler.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: "Runs 0–6", desc: "Click the run scored on the ball. 4s and 6s are automatically counted as boundaries." },
                  { label: "Wides (W, W+1, ...)", desc: "1 wide = 1 extra + no ball counted. W+2 = wide with 2 more runs off it." },
                  { label: "No Balls (NB+X)", desc: "1 no-ball extra + X runs to striker. Striker gets the runs, no ball counted." },
                  { label: "Leg Byes / Byes", desc: "Runs credited to team extras, not striker. Ball is counted." },
                  { label: "WICKET (OUT)", desc: "Choose dismissal type, fielder (optional), and the next batter in a modal." },
                  { label: "Swap Strike", desc: "Manually swap striker and non-striker. Automatic at end of over." },
                  { label: "Undo Last", desc: "Reverts the last ball. Use it right after a mis-click." },
                  { label: "Start 2nd Innings", desc: "Ends first innings, swaps batting/bowling teams and shows the openers modal again." },
                ].map((row, i) => (
                  <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="font-bold text-white">{row.label}</div>
                    <div className="text-sm text-slate-400 mt-1">{row.desc}</div>
                  </div>
                ))}
              </div>
              <Callout tone="good" title="End of over">
                After 6 legal deliveries the app automatically opens a "select next bowler" modal and swaps the strike.
              </Callout>
              <p className="mt-4"><b>Bottom Bar Control:</b> configure what the three zones of the scorebug's bottom bar display — Current RR / Required RR / Projected / 4s & 6s / Match Equation / Innings Status / Toss Info / Custom text / Extras / Last Over / Target.</p>
            </Section>

            <Section id="director" icon={Video} title="8. Director Switcher (top of controller)">
              <p>The dark bar with buttons at the top of the controller decides <b>which graphic is currently showing on-air</b>. Only one can be visible at a time.</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li><b>Scorebug</b> — the bottom score bar (default during play).</li>
                <li><b>Batting Summary</b> — full batting card, ideal at fall of wicket or drinks break.</li>
                <li><b>Bowling Summary</b> — bowlers' figures, ideal at innings break.</li>
                <li><b>Match Summary</b> — both teams' scores + top performers, use at end of innings / match.</li>
                <li><b>HIDE ALL (red)</b> — clears the screen (nothing on the overlay). Use during ads, boundary replays, and interviews.</li>
              </ul>
              <Callout tone="good">
                Click a button and the overlay updates in <b>&lt; 200ms</b> — safe to switch during a live cut.
              </Callout>
            </Section>

            <Section id="themes" icon={Palette} title="9. Graphics Themes">
              <p>Underneath the Director Switcher is the <b>🎨 Graphics Theme</b> row. Six themes, each with a live preview thumbnail:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ["Classic", "The original clean broadcast look with light bottom bar."],
                  ["Sky Live", "Italic bold text + yellow accent bar — inspired by Sky Sports."],
                  ["ESPN Clean", "White dominant with a red trim — inspired by ESPN Cricinfo."],
                  ["IPL Neon", "Dark gradient with gold accents and glowing centre pill."],
                  ["Broadcast Pro", "Modern dark slate with cyan/blue accent — premium feel."],
                  ["Minimalist", "Ultra-thin, monospace numbers, small footprint."],
                ].map(([name, desc]) => (
                  <div key={name} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="font-bold text-white">{name}</div>
                    <div className="text-sm text-slate-400 mt-1">{desc}</div>
                  </div>
                ))}
              </div>
              <Callout tone="info" title="How previews work">
                Each card contains a live mini-iframe of the overlay in that theme, using the same match state. You can watch how each look would appear before clicking to switch on-air.
              </Callout>
            </Section>

            <Section id="overlay" icon={Copy} title="10. Your Overlay URL">
              <p>Every match has a <b>public URL</b> that renders the graphics on a transparent background at 1920×1080. This is what vMix/OBS loads.</p>
              <Step n="1" title="Find the URL">Two places: the "Copy Overlay URL" button at the top of the controller, OR the same button on the match card back on your dashboard.</Step>
              <Step n="2" title="Click Copy">The URL is copied to your clipboard. It looks like: <code className="bg-slate-800 px-2 py-0.5 rounded text-xs">https://your-domain/overlay/&lt;matchId&gt;</code></Step>
              <Step n="3" title="Preview it">Click the small preview arrow icon to open the overlay in a new tab. You'll see the scorebug on a transparent (checkerboard-looking) background — that's normal.</Step>
              <Callout tone="warn" title="Do NOT share this URL publicly on social media">
                Anyone with the URL can see your live scoreboard. Only give it to your video crew.
              </Callout>
            </Section>

            <Section id="vmix" icon={Monitor} title="11. Setup in vMix">
              <Step n="1" title="Add Input → Web Browser">In vMix, click <b>Add Input</b> (bottom bar) → <b>Web Browser</b>.</Step>
              <Step n="2" title="Paste the overlay URL">Paste your copied URL into the URL field. Set width = 1920, height = 1080. Click OK.</Step>
              <Step n="3" title="Layer it over your live camera">Use vMix Multi-View: drag your Camera as Input 1, and the Web Browser input as Input 2 in a MultiView layer. The transparent overlay sits on top of the camera.</Step>
              <Step n="4" title="Verify chroma is not needed">Because the background is truly transparent (alpha=0), you do NOT need chroma-key. The graphics will render cleanly on top of any video.</Step>
              <Callout tone="good" title="Ready to broadcast">
                Send this MultiView to your Output. Anything you switch on the Cricket Graphics Studio director will appear instantly on your broadcast.
              </Callout>
            </Section>

            <Section id="obs" icon={Monitor} title="12. Setup in OBS Studio">
              <Step n="1" title="Add Source → Browser">Under Sources click <b>+</b> → <b>Browser</b> → Create New → OK.</Step>
              <Step n="2" title="Paste the URL, set size">URL = your overlay URL. Width = 1920, Height = 1080. Uncheck "Shutdown source when not visible" so it keeps listening for live updates.</Step>
              <Step n="3" title="Position it as the top layer">Drag the Browser source to the top of the Sources list. Your camera / video capture should be below it.</Step>
              <Step n="4" title="Fit to screen">Right-click the Browser source → Transform → Fit to Screen (Ctrl + F). Done.</Step>
              <Callout tone="info" title="If the graphic gets sluggish">
                Right-click the Browser source → Properties → Custom CSS: leave blank. Also toggle "Refresh browser when scene becomes active" once to force a re-connect to Firebase.
              </Callout>
            </Section>

            <Section id="youtube" icon={Youtube} title="13. Going Live on YouTube">
              <Step n="1" title="Create a live event on YouTube Studio">Go to <b>studio.youtube.com</b> → <b>Create</b> → <b>Go Live</b>. Set title, description, thumbnail, category (Sports), and visibility.</Step>
              <Step n="2" title="Copy your Stream Key">YouTube gives you a Stream Key + Stream URL under the "Stream" settings.</Step>
              <Step n="3" title="Paste into vMix / OBS">
                <div className="mt-2">
                  <b className="text-white">vMix:</b> Settings → Streaming → Add Destination → Custom RTMP Server → paste Stream URL + Stream Key.<br />
                  <b className="text-white">OBS:</b> Settings → Stream → Service: YouTube → Server: Primary → Stream Key: paste.
                </div>
              </Step>
              <Step n="4" title="Click Start Streaming">Wait a few seconds — YouTube Studio will show your feed in preview. When it looks good, click <b>Go Live</b> on YouTube.</Step>
              <Callout tone="good">
                Congratulations — your match is now live on YouTube with real-time graphics. Everyone who joins your stream sees the same scorebug and summaries.
              </Callout>
            </Section>

            <Section id="workflow" icon={ArrowRight} title="14. A Real-World Match-Day Workflow">
              <p>Here's the full sequence a professional broadcast crew uses:</p>
              <ol className="list-decimal list-inside space-y-3 pl-2">
                <li><b>Day before:</b> Log in, create the match, set overs, team colors, upload team logos, enter both lineups. Test the overlay URL in a browser to make sure both teams show correctly.</li>
                <li><b>1 hour before start:</b> Add the overlay URL as a Browser Source in vMix / OBS. Start streaming to YouTube in "Preview" (private) mode. Confirm the transparent graphic sits on top of the camera feed.</li>
                <li><b>15 min before toss:</b> Choose a theme (Sky Live and IPL Neon are crowd favourites). Push <b>HIDE ALL</b> so nothing is on-air yet.</li>
                <li><b>Toss:</b> Enter toss winner + decision. Switch to <b>Match Summary</b> for 30 seconds — coin icon 🪙 appears next to the toss winner.</li>
                <li><b>1st ball:</b> Click <b>Select Active Players</b> → set striker, non-striker, bowler. Push <b>Scorebug</b>. Start scoring ball-by-ball with the run buttons.</li>
                <li><b>Fall of wicket:</b> Push <b>Batting Summary</b> for 10 seconds → then back to Scorebug for the next ball. On the wicket modal, always confirm the next batter.</li>
                <li><b>End of over:</b> Push <b>Bowling Summary</b> for 5 seconds. When the next-bowler modal appears, pick the new bowler.</li>
                <li><b>Innings break:</b> Push <b>Match Summary</b>. Click <b>Start 2nd Innings</b>. Choose openers for the chasing team.</li>
                <li><b>Final ball:</b> Keep <b>Match Summary</b> up for a full minute with the result line auto-populated ("&lt;Team&gt; won by X runs/wickets").</li>
                <li><b>Post-match:</b> Push <b>HIDE ALL</b>, stop streaming.</li>
              </ol>
            </Section>

            <Section id="troubleshoot" icon={AlertTriangle} title="15. Troubleshooting">
              <div className="space-y-4">
                {[
                  {
                    q: "The overlay shows only 'Waiting for match data...'",
                    a: "You loaded the overlay before opening the controller. Open the match controller once — that pushes the initial state to Firebase. The overlay will connect automatically."
                  },
                  {
                    q: "My camera feed shows through the graphic in patches / the graphic looks transparent",
                    a: "Make sure you selected a theme (the graphics are always opaque). If you're still seeing leaks, refresh your vMix/OBS Browser source. In OBS: right-click source → Refresh cache of current page."
                  },
                  {
                    q: "Scores don't update in real time",
                    a: "Your machine may have blocked Firebase. Whitelist *.firebaseio.com in your firewall. Also ensure you're not on a corporate VPN blocking WebSockets."
                  },
                  {
                    q: "Team colors look wrong",
                    a: "Go to Team Config → Team Details, click Save Team Config again. The overlay will pick up the new hex color instantly."
                  },
                  {
                    q: "Can't log in after paying / renewing",
                    a: "Contact your admin — they need to extend your expiry date in the Admin Panel."
                  },
                  {
                    q: "How do I switch themes mid-match?",
                    a: "Just click a different theme card in the controller. Do it BETWEEN balls to be safe — the switch is instant."
                  },
                  {
                    q: "Overlay URL doesn't work in vMix",
                    a: "Ensure the URL starts with https://. Set Width=1920, Height=1080 in the Web Browser input. Enable 'Auto Restart' on the input."
                  },
                ].map((item, i) => (
                  <div key={i} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="font-heading font-bold text-white flex items-start gap-2">
                      <HelpCircle size={16} className="mt-1 flex-shrink-0 text-amber-400" />
                      <span>{item.q}</span>
                    </div>
                    <div className="text-sm text-slate-300 mt-2 ml-6">{item.a}</div>
                  </div>
                ))}
              </div>
              <Callout tone="info" title="Still stuck?">
                Contact your administrator or the platform support email. Include the match name and a screenshot — helps them help you faster.
              </Callout>
            </Section>

            <div className="mt-16 p-6 rounded-2xl bg-gradient-to-br from-blue-600/15 to-blue-800/10 border border-blue-800 text-center">
              <Sparkles className="mx-auto mb-3 text-blue-400" size={28} />
              <div className="font-heading text-2xl font-extrabold uppercase text-white">You're ready to broadcast!</div>
              <p className="text-slate-300 mt-2 max-w-xl mx-auto">Head back to your dashboard and create your first match. If something goes sideways during a live broadcast, the Troubleshooting section above solves 90% of issues.</p>
              <Link to="/matches" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 font-semibold text-white" data-testid="manual-back-to-matches">
                Go to My Matches <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
